import { Controller } from "@hotwired/stimulus";
import {
  applyAddOnToScenarioTotals,
  contributionForGap,
  currentTopUpAmount,
  dispatchPanelCosts,
} from "../scenario_modeling/contribution";
import { hideModelingLoading, showModelingLoading } from "../scenario_modeling/payload_refresh";
import { appendModelingControlParams } from "../scenario_modeling/request_params";
import {
  refreshVolBoughtValidation,
} from "../scenario_modeling/vol_bought_validation";

export default class extends Controller {
  static targets = [
    "volBoughtInput",
    "taxesLeviesInput",
    "otherCostsInput",
    "volBoughtValue",
    "taxesLeviesValue",
    "otherCostsValue",
    "contributionCell",
    "livingWageContributionCell",
    "salaryMatricesCell",
    "workersCell",
    "volDeclaredCell",
    "totalContributionValue",
    "livingWageContributionValue",
    "salaryMatricesValue",
    "workersValue",
    "volDeclaredValue",
    "volBoughtCell",
    "volBoughtReadonly",
    "gapClosureLabel",
  ];

  static values = {
    gapTarget: { type: Number, default: 100 },
    gapClosureTemplate: String,
    exchangeRatesUrl: String,
  };

  connect() {
    this._fetchInFlight = false;
    this._debounceTimer = null;
    this._onGapTargetChange = this.updateGapTarget.bind(this);
    this._onExchangeRatesLoading = this.showLoading.bind(this);
    this._onExchangeRatesReady = this.hideLoading.bind(this);
    this._onExchangeRatesError = this.restoreAfterError.bind(this);
    this._onTopUpChange = () => applyAddOnToScenarioTotals();

    window.addEventListener("scenario-modeling:gap-target-change", this._onGapTargetChange);
    window.addEventListener("scenario-modeling:exchange-rates-loading", this._onExchangeRatesLoading);
    window.addEventListener("scenario-modeling:exchange-rates-ready", this._onExchangeRatesReady);
    window.addEventListener("scenario-modeling:exchange-rates-error", this._onExchangeRatesError);
    window.addEventListener("scenario-modeling:top-up-change", this._onTopUpChange);

    const gapInput = document.getElementById("scenario_lw_gap_closure_target");
    if (gapInput) this.gapTargetValue = this._normalizeGapTarget(gapInput.value);

    this._renderContributions();
    this._renderGapClosureLabel();
    refreshVolBoughtValidation(this.element);
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);

    window.removeEventListener("scenario-modeling:gap-target-change", this._onGapTargetChange);
    window.removeEventListener("scenario-modeling:exchange-rates-loading", this._onExchangeRatesLoading);
    window.removeEventListener("scenario-modeling:exchange-rates-ready", this._onExchangeRatesReady);
    window.removeEventListener("scenario-modeling:exchange-rates-error", this._onExchangeRatesError);
    window.removeEventListener("scenario-modeling:top-up-change", this._onTopUpChange);
  }

  recalculateTotals() {
    if (this.hasVolBoughtValueTarget) {
      this._updateTotal(this.volBoughtInputTargets, this.volBoughtValueTarget);
    }
    if (this.hasTaxesLeviesValueTarget) {
      this._updateTotal(this.taxesLeviesInputTargets, this.taxesLeviesValueTarget);
    }
    if (this.hasOtherCostsValueTarget) {
      this._updateTotal(this.otherCostsInputTargets, this.otherCostsValueTarget);
    }
    this._renderContributions();
    this._updateVolBoughtValidation();
    this._schedulePersist();
  }

  _updateVolBoughtValidation() {
    refreshVolBoughtValidation(this.element);
  }

  _schedulePersist() {
    if (!this.exchangeRatesUrlValue) return;
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._persistInputs(), 400);
  }

  async _persistInputs() {
    if (this._fetchInFlight || !this.exchangeRatesUrlValue) return;

    this._fetchInFlight = true;

    try {
      const url = new URL(this.exchangeRatesUrlValue, window.location.origin);
      const params = new URLSearchParams();
      appendModelingControlParams(params);
      url.search = params.toString();

      await fetch(url, {
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      });
    } catch (_error) {
      // Ignore autosave failures; explicit navigation still saves.
    } finally {
      this._fetchInFlight = false;
    }
  }

  updateGapTarget(event) {
    this.gapTargetValue = this._normalizeGapTarget(event.detail?.value);
    this._renderContributions();
    this._renderGapClosureLabel();
  }

  showLoading() {
    showModelingLoading();
  }

  hideLoading() {
    hideModelingLoading();
  }

  restoreAfterError() {
    hideModelingLoading();
  }

  _renderGapClosureLabel() {
    if (!this.hasGapClosureLabelTarget || !this.gapClosureTemplateValue) return;

    this.gapClosureLabelTarget.textContent = this.gapClosureTemplateValue.replace(
      "%{percent}",
      String(Math.round(this.gapTargetValue)),
    );
  }

  _renderContributions({ skipDispatch = false } = {}) {
    let totalTaxes = 0;
    let totalOther = 0;

    if (this.hasTaxesLeviesInputTarget) {
      totalTaxes = this.taxesLeviesInputTargets.reduce(
        (sum, input) => sum + this._parseInputValue(input),
        0,
      );
    }

    if (this.hasOtherCostsInputTarget) {
      totalOther = this.otherCostsInputTargets.reduce(
        (sum, input) => sum + this._parseInputValue(input),
        0,
      );
    }

    this.livingWageContributionCellTargets.forEach((cell) => {
      const gap = Number(cell.dataset.gapPayment) || 0;
      const amount = cell.querySelector("[data-role='amount']");
      if (amount) amount.textContent = contributionForGap(gap, this.gapTargetValue, 0, 0).toLocaleString();
    });

    const panelCosts = this.contributionCellTargets.map((cell) => {
      const gap = Number(cell.dataset.gapPayment) || 0;
      const userId = cell.dataset.userId;
      const taxes = this._costInputValue("taxes_levies", userId);
      const other = this._costInputValue("other_costs", userId);
      const contribution = contributionForGap(gap, this.gapTargetValue, taxes, other);
      const amount = cell.querySelector("[data-role='amount']");
      if (amount) amount.textContent = contribution.toLocaleString();

      this._broadcastCosts(userId, taxes, other);
      return { panelId: cell.dataset.panelId, taxes, other };
    });
    dispatchPanelCosts(panelCosts);

    const footerGap = this.hasTotalContributionValueTarget
      ? Number(this.totalContributionValueTarget.dataset.gapPayment) || 0
      : 0;

    if (this.hasLivingWageContributionValueTarget) {
      this.livingWageContributionValueTarget.textContent = contributionForGap(
        footerGap,
        this.gapTargetValue,
        0,
        0,
      ).toLocaleString();
    }

    if (this.hasTotalContributionValueTarget) {
      const base = contributionForGap(footerGap, this.gapTargetValue, totalTaxes, totalOther);
      this.totalContributionValueTarget.dataset.baseContribution = String(base);
      this.totalContributionValueTarget.textContent = (base + currentTopUpAmount()).toLocaleString();
    }

    if (!skipDispatch) this._dispatchContributionsUpdated();
  }

  _dispatchContributionsUpdated() {
    let totalContribution = 0;
    let livingWageContribution = 0;

    if (this.hasTotalContributionValueTarget) {
      totalContribution = this._parseInputValue({
        value: this.totalContributionValueTarget.textContent.replace(/,/g, ""),
      });
      const gap = Number(this.totalContributionValueTarget.dataset.gapPayment) || 0;
      livingWageContribution = contributionForGap(gap, this.gapTargetValue, 0, 0);
    }

    window.dispatchEvent(
      new CustomEvent("scenario-modeling:contributions-updated", {
        detail: { totalContribution, livingWageContribution },
      }),
    );
  }

  _broadcastCosts(userId, taxesLevies, otherCosts) {
    if (!userId) return;

    window.dispatchEvent(
      new CustomEvent("scenario-modeling:costs-change", {
        detail: { panelId: String(userId), taxesLevies, otherCosts },
      }),
    );
  }

  _costInputValue(field, userId) {
    const input = this.element.querySelector(
      `input[name="scenario[intermediary_modeling_inputs][${userId}][${field}]"]`,
    );
    if (!input) return 0;

    return this._parseInputValue(input);
  }

  _updateTotal(inputs, outputTarget) {
    if (!outputTarget) return;

    const total = inputs.reduce((sum, input) => sum + this._parseInputValue(input), 0);
    outputTarget.textContent = this._formatNumber(total);
  }

  _parseInputValue(input) {
    const value = Number.parseFloat(input.value);
    return Number.isFinite(value) ? value : 0;
  }

  _formatNumber(value) {
    const number = Number(value) || 0;
    if (Math.abs(number - Math.round(number)) < 1e-9) {
      return Math.round(number).toLocaleString();
    }

    return number.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }

  _normalizeGapTarget(raw) {
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return 0;

    return Math.min(100, Math.max(0, value));
  }
}
